import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatCardFooter } from '@angular/material/card';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { CardComponent, ExportCsvOptionsComponent } from '@vectronic/ui';
import { DialogService, MobileService } from '@vectronic/util';
import { first, merge, of, skip, switchMap, tap } from 'rxjs';
import { ColumnService } from '../../services/column.service';
import { DatatableContextService } from '../../services/datatable-context.service';
import { FilterService } from '../../services/filter.service';
import { TableSelectionService } from '../../services/table-selection.service';
import { TableExport } from '../../services/table.export';
import { DisplayColumn } from '../../types/datatable-column';
import { DatatableConfig } from '../../types/datatable-configuration';
import { DatatableBodyComponent } from '../datatable-body/datatable-body.component';
import { DatatableFiltersComponent } from '../datatable-filters/datatable-filters.component';
import { DatatableHeaderComponent } from '../datatable-header/datatable-header.component';
import { DatatablePaginatorComponent } from '../datatable-paginator/datatable-paginator.component';

@Component({
  selector: 'table-datatable',
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  imports: [
    CommonModule,
    CardComponent,
    DatatableHeaderComponent,
    DatatableBodyComponent,
    DatatablePaginatorComponent,
    MatCardFooter,
    DatatableFiltersComponent,
  ],
  providers: [DatatableContextService, FilterService, ColumnService],
})
export class DatatableComponent<T extends object> implements OnInit {
  private tableSelectionService = inject(TableSelectionService);
  private dialogService = inject(DialogService);
  private datatableContextService = inject(DatatableContextService);
  filterService = inject(FilterService<T>);
  columnService = inject(ColumnService<T>);
  mobileService = inject(MobileService);

  datatableConfig = input.required<DatatableConfig<T>>();
  data = input.required<T[]>();
  dataCount = input<number>();
  displayColumns = input<DisplayColumn<T>[]>();
  isLoading = input<boolean>();
  loadingProgress = input<number>();
  isLoadingCSV = input<boolean>();
  titleExtension = input('');
  dynamicTableTitle = input(undefined);
  pageIndex = model(0);
  /**
   * Initial page size to apply once persisted column settings are loaded.
   * When provided, it overrides the persisted / configured page size.
   */
  pageSize = input<number>();
  exportSelectedTrigger = input<boolean>();
  exportSelectedEffect = effect(() => {
    if (this.exportSelectedTrigger() != undefined) untracked(() => this.exportCsv(true));
  });

  columnReorder = output<DisplayColumn<T>[]>();
  pageChanged = output<PageEvent>();
  rowClicked = output<T>();
  tableSort = output<Sort>();
  searchEvent = output<string>();

  @ContentChild('headerButtons') headerButtons: TemplateRef<any>;

  isInitialLoading = signal(true);
  private filtersInitialized = signal(false);
  private lastAppliedSelectionFilterSignature = signal<string | undefined>(undefined);

  searchConfig = computed(
    () => this.datatableConfig()?.search as Exclude<DatatableConfig<T>['search'], boolean>,
  );
  paginationConfig = computed(() => this.datatableConfig()?.pagination);
  filterConfig = computed(() => this.datatableConfig()?.filter);
  activeSelectionFilterId = computed(() => this.filterConfig()?.selectedSelectionFilter);
  extraFilterValuesSignature = computed(() =>
    JSON.stringify(this.filterConfig()?.extraFilterValues ?? {}),
  );

  setFilterService = effect(() => this.filterService.set(this.datatableConfig()));
  toggleFilterOnSelectionEffect = effect(() => {
    const filterConfig = this.filterConfig();
    if (!filterConfig?.toggleOnSelection) return;
    const hasSelection = Boolean(
      this.tableSelectionService.selectionLength() ||
      this.tableSelectionService.singleSelectionSignal(),
    );
    untracked(() => {
      if (hasSelection) this.filterService.doShow.set(false);
      else if (this.filterService.loadShowFilter()) this.filterService.doShow.set(true);
    });
  });
  applySelectionFilterPreset = effect(() => {
    if (!this.filtersInitialized()) return;

    const selectedValue = this.activeSelectionFilterId();
    const signature = this.selectionFilterSignature(selectedValue);
    if (signature === this.lastAppliedSelectionFilterSignature()) return;

    untracked(() => {
      const preset = this.filterConfig()?.selectionFilters?.find(({ id }) => id === selectedValue);
      const filterValues = preset?.filterValues;
      if (!filterValues) return;
      this.filterService.applyFilterValues(filterValues);
      this.lastAppliedSelectionFilterSignature.set(signature);
    });
  });

  setColumnService = effect(() => {
    this.columnService.datatableConfig.set(this.datatableConfig());
    this.columnService.inputDisplayColumns.set(this.displayColumns());
  });

  hasData = computed(
    () => (this.isLoading() || this.computedDataCount() > 0) && Boolean(this.data()),
  );

  filteredData = computed(() => this.filterService.applyFilters(this.data()));

  searchSignal = signal<string>(undefined);

  searchedData = computed(() => {
    if (this.searchSignal()) return this.searchData(this.searchSignal(), this.filteredData());
    return this.filteredData();
  });

  resetPageOnSearch = effect(() => {
    this.searchSignal();
    this.pageIndex.set(0);
  });

  resetPageOnFilter = effect(() => {
    this.filterService.activeFilters();
    this.extraFilterValuesSignature();
    this.pageIndex.set(0);
    untracked(() => this.deselectTable());
  });

  computedDataCount = computed(() => this.dataCount?.() ?? this.searchedData()?.length);
  maxPageIndex = computed(() =>
    Math.ceil(this.computedDataCount() / this.columnService.pageSize()),
  );

  isLoadingUntilScrollUp = signal(false);

  onFilterChange(event: { title: string; value: unknown }) {
    this.filterService.onFilterChange(event);
    this.lastAppliedSelectionFilterSignature.set(
      this.selectionFilterSignature(this.activeSelectionFilterId()),
    );
  }

  private selectionFilterSignature(selectedValue: number | undefined) {
    const preset = this.filterConfig()?.selectionFilters?.find(({ id }) => id === selectedValue);
    const filterValues = preset?.filterValues;
    return filterValues
      ? JSON.stringify({
          id: selectedValue,
          filterValues,
          extraFilterValues: this.extraFilterValuesSignature(),
        })
      : undefined;
  }

  constructor() {
    const data$ = toObservable(this.data).pipe(skip(1));
    const searchChanged$ = toObservable(this.searchSignal).pipe(skip(1));
    const pageIndexChanged$ = toObservable(this.pageIndex).pipe(skip(1));
    const filtersChanged$ = toObservable(this.filterService.activeFilters).pipe(skip(1));
    const extraFilterValuesChanged$ = toObservable(this.extraFilterValuesSignature).pipe(skip(1));
    merge(searchChanged$, pageIndexChanged$, filtersChanged$, extraFilterValuesChanged$)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.datatableContextService.notifyContextChanged());

    this.datatableContextService.contextChanged$
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.isLoadingUntilScrollUp.set(true)),
        switchMap(() => {
          this.isLoadingUntilScrollUp.set(false);
          // if standard search is enabled, it should perform the scroll to top immediately, hence of([])
          return (
            this.searchConfig()?.internal || this.filterService.hasFilters() ? of([]) : data$
          ).pipe(
            first(),
            tap(() => {
              this.scrollToTop();
              this.isLoadingUntilScrollUp.set(false);
            }),
          );
        }),
      )
      .subscribe();
  }

  private scrollToTop() {
    const innerVirtualScrollViewport = document?.getElementById('innerVirtualScrollViewport');
    if (innerVirtualScrollViewport) innerVirtualScrollViewport.scrollTop = 0;
  }

  exportCsv(doSelected?: boolean) {
    const datatableConfig = this.datatableConfig();
    const selection = this.tableSelectionService.selectionSignal();
    let data = this.searchedData();
    if (doSelected) {
      const selectBy = datatableConfig.select?.key ?? datatableConfig.select?.multi;
      data = data.filter((d) => selection.includes(d[selectBy]));
    }
    this.dialogService.open(ExportCsvOptionsComponent, {
      data: {
        csvSeparator: ',',
        hideMultipleFile: true,
        hintText: datatableConfig.csvExportHintText,
      },
      afterConfirm: (exportOptions) =>
        TableExport.csv({
          data,
          columnOrder: this.columnService.displayColumns(),
          exportOptions,
          datatableConfig,
        }),
    });
  }

  onColumnSelectionChange(event: DisplayColumn<T>[]) {
    this.columnService.setColumnSelection(event);
    this.columnReorder.emit(this.columnService.displayColumns());
  }

  onColumnReorder(event: CdkDragDrop<string[]>) {
    this.columnService.reorderColumns(event);
    this.columnReorder.emit(this.columnService.displayColumns());
  }

  ngOnInit() {
    this.filterService.init(this.datatableConfig());
    this.filtersInitialized.set(true);
    this.columnService.loadColumns(this.datatableConfig(), this.displayColumns()).subscribe(() => {
      if (this.pageSize() != null) this.columnService.setPageSize(this.pageSize());
      this.columnReorder.emit(this.columnService.displayColumns());
      this.pageChanged.emit({
        pageSize: this.columnService.pageSize(),
        pageIndex: this.pageIndex(),
      } as PageEvent);
      this.isInitialLoading.set(false);
    });
  }
  onPageChanged(event: PageEvent) {
    const oldPageSize = this.columnService.rawPageSize();
    const oldPageIndex = this.pageIndex();
    this.columnService.setPageSize(event.pageSize);
    if (oldPageSize != event.pageSize) this.columnService.storeTableSettings();
    this.pageIndex.set(event.pageIndex);
    this.pageChanged.emit(event);
    if (oldPageIndex != event.pageIndex || oldPageSize > event.pageSize) this.deselectTable();
  }

  onPageIndexChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.value == '') target.value = 1 as any;
    const pageIndex = parseInt(target.value) - 1;
    this.pageIndex.set(pageIndex);
    this.pageChanged.emit({
      length: this.computedDataCount(),
      pageIndex,
      pageSize: this.columnService.rawPageSize(),
    });
    target.blur();
    this.deselectTable();
  }

  private deselectTable() {
    const { key, multi, single, highlightKey, doHighlight, doMultiHighlight } =
      this.datatableConfig()?.select ?? {};
    if (!doHighlight && !doMultiHighlight && (key || multi || single)) {
      this.tableSelectionService.deselectAll?.();
    }
    if (highlightKey || doHighlight || doMultiHighlight) {
      this.tableSelectionService.clearHighlight?.();
    }
  }

  onSearchFieldChanged(event: string) {
    if (this.searchConfig()?.internal) {
      this.searchSignal.set(event);
      this.deselectTable();
    }
    this.searchEvent.emit(event);
  }

  searchData(textFilter: string, datas: T[]) {
    const separators = this.searchConfig()?.separators ?? [','];
    const separatorRegex = new RegExp(separators.map((separator) => `\\${separator}`).join('|'));
    const textFilters = textFilter.split(separatorRegex).map((text) => text.trim().toLowerCase());
    const allColumns = this.datatableConfig()?.columns;
    const columns = this.columnService
      .displayColumns()
      .filter((name) => name != 'rowActions')
      .map((_fieldName) => allColumns.find(({ fieldName }) => fieldName === _fieldName))
      .filter(({ disableStandardSearch }) => !disableStandardSearch);
    return datas.filter((data) => {
      for (const filter of textFilters) {
        for (const column of columns) {
          const value =
            column.searchString?.(data) ??
            column.translator?.(data) ??
            data[column.fieldName as any];
          if (value?.toString().toLowerCase().includes(filter)) return true;
        }
      }
      return false;
    });
  }

  datatableWidth = signal(0);
  datatableCard = viewChild.required('datatableCard', { read: ElementRef });
  private resizeObserver: ResizeObserver;
  resize = effect((onCleanup) => {
    if (this.datatableConfig().disablePagination || typeof ResizeObserver == 'undefined') return;
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(([entry]) =>
        untracked(() => this.datatableWidth.set(entry.contentRect.width)),
      );
    }
    const element = this.datatableCard()?.nativeElement;
    if (element) this.resizeObserver.observe(element);
    onCleanup(() => this.resizeObserver?.disconnect());
  });
}
